/**
 * @module AdapterStatusBadge
 * @package @geenius/adapters/react-css
 * @description Renders the vanilla CSS adapter status badge for the React CSS
 * variant. The badge exposes the shared adapter status vocabulary in a compact,
 * themeable visual treatment.
 */

import type { AdapterStatusType } from "@geenius/adapters";
import type { JSX } from "react";
import { cx } from "../lib/cx";

/**
 * Props for rendering the React CSS adapter status badge.
 *
 * @property status Shared adapter status vocabulary value to display.
 * @property showLabel Whether the human-readable label should be rendered.
 * @property size Visual size variant — `sm` for list cells, `md` for detail headers (mirrors the React reference's size axis).
 * @property className Optional class name merged into the badge wrapper.
 */
export interface AdapterStatusBadgeProps {
  status: AdapterStatusType;
  showLabel?: boolean;
  size?: "sm" | "md";
  className?: string;
}

const STATUS_LABELS: Record<AdapterStatusType, string> = {
  connected: "Connected",
  disconnected: "Disconnected",
  error: "Error",
  initializing: "Initializing",
};

/**
 * Renders a compact React CSS badge for the current adapter lifecycle state.
 *
 * @param props Badge props including the status token, optional label visibility, and size variant.
 * @returns A styled badge element that mirrors the shared adapter status taxonomy.
 */
export function AdapterStatusBadge({
  status,
  showLabel = true,
  size = "sm",
  className = "",
}: AdapterStatusBadgeProps): JSX.Element {
  const wrapperClass = cx(
    "gn-adapters-badge",
    `gn-adapters-badge--${status}`,
    `gn-adapters-badge--${size}`,
    className,
  );

  // Icon-only branch: matches the React reference's role="img" + aria-label
  // contract so the dot is announced as a labelled image rather than skipped.
  if (!showLabel) {
    return (
      <span
        aria-label={STATUS_LABELS[status]}
        className={wrapperClass}
        role="img"
      >
        <span aria-hidden="true" className="gn-adapters-badge__dot" />
      </span>
    );
  }

  return (
    <span className={wrapperClass}>
      <span aria-hidden="true" className="gn-adapters-badge__dot" />
      <span>{STATUS_LABELS[status]}</span>
    </span>
  );
}
